import { Injectable } from "@angular/core";
import { GridService } from "../shared/grid/grid.service";
import { CreatePayload } from "../shared/grid/crud.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import {
    AgentJurisdiction,
    AgentJurisdictionRecordHistory,
    AgentsOverviewData,
    JurisdictionsOverviewData,
} from "../models/agent-jurisdiction.model";
import { catchError, Observable, of } from "rxjs";
import { ToastService } from "./toast.service";
import { GridModel } from "../shared/grid/grid.model";

@Injectable({
    providedIn: "root",
})
export class AgentJurisdictionService extends GridService<AgentJurisdiction> {
    constructor(
        http: HttpClient,
        private toastService: ToastService,
    ) {
        super("AgentJurisdiction", http);
    }

    createVersion(model: CreatePayload<AgentJurisdiction>): Observable<AgentJurisdiction> {
        return this.http.post<AgentJurisdiction>(
            this.getApiUrl(`${this.controllerName}/CreateVersion`),
            model,
        );
    }

    getHistory(params: HttpParams): Observable<GridModel<AgentJurisdictionRecordHistory>> {
        return this.http.get<GridModel<AgentJurisdictionRecordHistory>>(
            this.getApiUrl(`${this.controllerName}/GetHistory`),
            { params: params },
        );
    }

    getAgentDetails(agentId: string) {
        return this.http
            .get<
                AgentJurisdiction[]
            >(this.getApiUrl(`${this.controllerName}/GetAgentDetails?agentId=${agentId}`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading Agent Details",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getAgentGroupDetails(agentGroupId: string) {
        return this.http
            .get<
                AgentJurisdiction[]
            >(this.getApiUrl(`${this.controllerName}/GetAgentGroupDetails?agentGroupId=${agentGroupId}`))
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading Agent Details",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getJurisdictionDetails(jurisdictionId: string, organizationLevelId?: string) {
        // When an org level (the RA's selected site) is supplied, the API restricts the
        // results to agents actually added to that site via AgentOrgLevel, so the OEL-gap warning
        // no longer flags agents that will never appear in the risk assessment.
        let params = new HttpParams().set("jurisdictionId", jurisdictionId);
        if (organizationLevelId) {
            params = params.set("organizationLevelId", organizationLevelId);
        }
        return this.http
            .get<AgentJurisdiction[]>(
                this.getApiUrl(`${this.controllerName}/GetJurisdictionDetails`),
                { params },
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading Agent Details",
                        true,
                    );
                    return of([]);
                }),
            );
    }

    getJurisdictionsOverviewData(orgLevelId: string) {
        return this.http
            .get<JurisdictionsOverviewData>(
                this.getApiUrl(
                    `${this.controllerName}/GetJurisdictionsOverviewData?orgLevelId=${orgLevelId}`,
                ),
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading Agent Details",
                        true,
                    );
                    return of(new JurisdictionsOverviewData());
                }),
            );
    }

    getAgentsOverviewData(orgLevelId: string) {
        return this.http
            .get<AgentsOverviewData>(
                this.getApiUrl(
                    `${this.controllerName}/GetAgentsOverviewData?orgLevelId=${orgLevelId}`,
                ),
            )
            .pipe(
                catchError((error) => {
                    this.toastService.displayErrorMessage(
                        "Error loading Agent Details",
                        true,
                    );
                    return of(new AgentsOverviewData());
                }),
            );
    }
}
